import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { MouseEvent, useState } from 'react';
import { createStyles } from './SubmissionsSidebar.styles';
import ManageExtension from '../ManageExtension/ManageExtension';
import {
  ExtensionGroup,
  ExtensionUser,
} from '../ManageExtension/ManageExtension.types';

type SubmissionAssignment = {
  description?: string;
  endDate?: string;
  gradesReleasedAt?: string;
  gradeType?: string;
  id: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  objectId?: string;
  parentAssignmentId?: string;
  provider?: string;
  shouldCloseOnDueDate?: boolean;
  startDate?: string;
  studentsCount?: number;
  students?: string[];
  submissions?: string[];
  title: string;
};

type Props = {
  accessToken?: string;
  assignment?: SubmissionAssignment;
  assignmentCreationError?: any;
  assignmentUpdateError?: any;
  backRoute: string;
  createExtension: (params: any) => Promise<SubmissionAssignment | null>;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  extensions: SubmissionAssignment[];
  loadStudents: (spaceId: string) => Promise<any[]>;
  onExtensionSaved: () => Promise<void>;
  resetExtensionErrors: () => void;
  spaceId: string;
  spaceGroups: ExtensionGroup[];
  user: ExtensionUser | null;
};

const SubmissionSidebar = ({
  accessToken,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  backRoute,
  createExtension,
  editExtension,
  extensions,
  loadStudents,
  onExtensionSaved,
  resetExtensionErrors,
  spaceId,
  spaceGroups,
  user,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDark = theme.palette.mode === 'dark';
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedExtension, setSelectedExtension] =
    useState<SubmissionAssignment>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuExtension, setMenuExtension] = useState<SubmissionAssignment>();

  const closeExtension = () => {
    setIsOpen(false);
    setSelectedExtension(undefined);
  };

  const openMenu = (
    event: MouseEvent<HTMLButtonElement>,
    extension: SubmissionAssignment
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuExtension(extension);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuExtension(undefined);
  };

  const openEditExtension = () => {
    if (!menuExtension) return;
    setSelectedExtension(menuExtension);
    setIsOpen(true);
    closeMenu();
  };

  return (
    <>
      <Box sx={styles.sidebarWrapper}>
        <Box sx={styles.sidebarNavigation}>
          <NavigationBackHeader route={backRoute} />
        </Box>
        <Box sx={styles.sidebarHeader}>
          <Typography variant="h6" component="h2" sx={{ my: '16px' }}>
            {assignment?.title || 'Submissions'}
          </Typography>
          <Button
            type="button"
            onClick={() => {
              setSelectedExtension(undefined);
              setIsOpen(true);
            }}
            sx={styles.newExtension}
          >
            <Icons.Add />
            New extension
          </Button>
        </Box>
        <Box component="ul" sx={styles.sidebarContent}>
          <Box sx={styles.extensions}>
            {extensions.map((extension) => {
              const isExtension = Boolean(extension.parentAssignmentId);
              return (
                <Box
                  component="li"
                  key={extension.id}
                  sx={styles.extensionItem}
                >
                  {isExtension && (
                    <Icons.ExtensionOutlined
                      fontSize="medium"
                      sx={styles.extensionIcon}
                      aria-hidden
                    />
                  )}
                  <Typography sx={styles.extensionTitle}>
                    {isExtension ? extension.title : 'Main cohort'} (
                    {extension.submissions?.length || 0}/
                    {extension.studentsCount || 0})
                  </Typography>
                  {isExtension && (
                    <Tooltip title="Extension options">
                      <Box component="span" sx={styles.menuButtonWrapper}>
                        <IconButton
                          id={`extension-options-${extension.id}`}
                          type="button"
                          aria-label={`Options for ${extension.title}`}
                          aria-controls={
                            menuExtension?.id === extension.id
                              ? 'extension-options-menu'
                              : undefined
                          }
                          aria-expanded={
                            menuExtension?.id === extension.id || undefined
                          }
                          aria-haspopup="menu"
                          sx={styles.menuIcon}
                          onClick={(event) => openMenu(event, extension)}
                        >
                          <Icons.MoreVert />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                  <IconButton
                    type="button"
                    aria-label={`${extension.isHidden ? 'Unhide' : 'Hide'} ${
                      isExtension ? extension.title : 'Main cohort'
                    }`}
                    sx={styles.visibilityIcon}
                    onClick={() =>
                      editExtension({
                        accessToken,
                        assignment: {
                          ...extension,
                          isHidden: !extension.isHidden,
                        },
                        spaceId,
                      })
                    }
                  >
                    {extension.isHidden ? (
                      <Icons.VisibilityOffOutlined />
                    ) : (
                      <Icons.VisibilityOutlined />
                    )}
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Menu
          id="extension-options-menu"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeMenu}
          aria-labelledby={
            menuExtension ? `extension-options-${menuExtension.id}` : undefined
          }
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            style: {
              backgroundColor: isDark ? '#363636' : undefined,
              color: isDark ? '#ffffff' : undefined,
              marginTop: '4px',
              width: 'max-content',
            },
          }}
          sx={styles.extensionMenu}
        >
          <MenuItem
            icon={<Icons.EditOutlined fontSize="small" />}
            label="Edit"
            onClick={openEditExtension}
          />
          <MenuItem
            icon={<Icons.DeleteOutlined fontSize="small" />}
            label="Delete"
          />
        </Menu>
      </Box>
      {assignment && user && (
        <ManageExtension
          accessToken={accessToken}
          assignment={assignment}
          assignmentCreationError={assignmentCreationError}
          assignmentUpdateError={assignmentUpdateError}
          createExtension={createExtension}
          editExtension={editExtension}
          extension={selectedExtension}
          isOpen={isOpen}
          loadStudents={loadStudents}
          onExtensionSaved={onExtensionSaved}
          onCloseModal={closeExtension}
          resetExtensionErrors={resetExtensionErrors}
          spaceId={spaceId}
          spaceGroups={spaceGroups}
          user={user}
        />
      )}
    </>
  );
};

export default SubmissionSidebar;
